import { Drawer, Card, CardHeader, CardContent, useMediaQuery, Box, Button, FormHelperText, Grid, MenuItem } from "@mui/material";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import SelectField from "../formik/SelectField";
import ReadonlyField from "../input/ReadonlyField";
import { RecPatch } from "@/dataProvider/tools";
import { CreationAssigment, PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import ClassificationDARAssignment from "./ClassificationDARAssignment";


const validationSchema =
  Yup.object().shape({
    primaryCls: Yup.number().required('Primary Classification is required'),
    secondaryCls: Yup.string().max(100, 'Secondary Classification must be at most 100 characters').required('Secondary Classification is required'),
    description: Yup.string().max(512),
  })


type ClassificationDto = SecondaryClassificationResponseDto;

export interface EditClassificationProps {
  open: boolean;
  setOpen: (x: boolean) => void;
}

const EditClassification: FC<EditClassificationProps> = ({ open, setOpen }) => {
  const match = useMediaQuery('(max-width: 580px)')
  const width = match ? 'calc(100vw - 50px)' : 530
  
  const record = useRecordContext<ClassificationDto & RecPatch>()
  const mode = record !== null && record !== undefined ? 'edit' : 'create'
    
  const save = useSimpleSaveAction()
  const initialValues = {
    submit: '',
    primaryCls: record?.primaryClassification !== undefined ? record.primaryClassification : '',
    secondaryCls: record?.name || '',
    description: record?.description || '',
  }

  const recordRef = useRef<ClassificationDto | null>(record)
  const [createStates, setCreateStates] = useState({
    darAssignment: null as CreationAssigment|null
  })

  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setOpen(false)
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const toSave: ClassificationDto&RecPatch =
      mode === 'create'
      ? { 
        primaryClassification: Number.parseInt(values.primaryCls as any) as PrimaryClassification, 
        primaryClassificationText: PrimaryClassification[values.primaryCls as PrimaryClassification], 
        name: values.secondaryCls, 
        description: values.description, 
        ...createStates,
        id: '',
      }
      : { ...record, name: values.secondaryCls, description: values.description }
    save(toSave, options)
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, setValues, resetForm } = formikCtrl
  const handleClose = useCallback(() => { 
    setOpen(false) 
    recordRef.current = null
    resetForm({
      values: { primaryCls: '', secondaryCls: '', description: '', submit: '', }
    })
  }, [setOpen, resetForm])

  useEffect(() => {
    if (!recordRef.current && !!record) {
      recordRef.current = record
      setValues({
        primaryCls: record?.primaryClassification !== undefined ? record.primaryClassification : '',
        secondaryCls: record?.name || '',
        description: record.description || '',
      } as any)
    }
  }, [record?.id, setValues])

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <OdForm formikCtrl={formikCtrl}>
        <Card sx={{ overflowY: 'auto', mb: 4, }} >
          <CardHeader title={mode === 'create' ? "New Data Classification" : "Edit Data Classification"} />
          <CardContent>

            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} >
                {mode === 'edit' ?
                  <ReadonlyField label="Primary Classification*" value={record.primaryClassificationText || ''} /> :
                  <SelectField label="Primary Classification*" name="primaryCls" >
                    <MenuItem value="0">Public</MenuItem>
                    <MenuItem value="1">Sensitive</MenuItem>
                    <MenuItem value="2">Confidential</MenuItem>
                    <MenuItem value="3">Regulated</MenuItem>
                  </SelectField>
                }
              </Grid>
              <Grid item xs={12} >
                <TextField label="Secondary Classification*" name="secondaryCls" />
              </Grid>
              <Grid item xs={12} >
                <TextField label="Description" name="description" multiline={true} />
              </Grid>
              {mode === 'create' &&
                <Grid item xs={12}>
                  <ClassificationDARAssignment setDarAssignment={(x) => setCreateStates({ darAssignment: x })} />
                </Grid>}
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Card>
      </OdForm>
    </Drawer>
  )
}

export default EditClassification;
